import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { ALL_MODES, FormType, ModeType } from '../EngagementDetailsEnums'
import QualityMode from '../../../../assets/images/quality-mode.svg?react'
import InspectMode from '../../../../assets/images/inspect-mode.svg?react'
import CareMode from '../../../../assets/images/care-mode.svg?react'
import DropDownArrow from '../../../../assets/images/drop-down-arrow.svg?react'
import { Box, Button, Typography } from '@mui/material'
import { Dropdown } from './Dropdown'
import { useLocation, useNavigate } from 'react-router-dom'
import { useCanNavigateTo } from '../../../../hooks/useCanNavigateTo'

interface ModeDropdownProps {
  currentMode: ModeType
}

export const ModeDropdown: FC<ModeDropdownProps> = ({ currentMode }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const isAuthorized = useCanNavigateTo()

  const basePath = location.pathname.replace(/\/mode\/[^/]+$/, '')

  const authorizedModes = useMemo(() => ALL_MODES.filter(mode => isAuthorized(`${basePath}/mode/${mode.toLowerCase()}`)), [isAuthorized, basePath])

  const [shouldOpenDropdown, setShouldOpenDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShouldOpenDropdown(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShouldOpenDropdown(false)
      }
    }
    if (shouldOpenDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [shouldOpenDropdown])

  if (authorizedModes.length <= 1) return null

  const isModeType = (value: ModeType | FormType): value is ModeType => Object.values(ModeType).includes(value as ModeType)

  const getModeIcon = (option: ModeType | FormType) => {
    if (!isModeType(option)) return null
    switch (option) {
      case ModeType.QA:
        return <QualityMode aria-hidden="true" />
      case ModeType.Inspect:
        return <InspectMode aria-hidden="true" />
      case ModeType.CustomerCare:
        return <CareMode aria-hidden="true" />
    }
  }

  const handleModeSelect = (selectedMode: ModeType | FormType) => {
    if (!isModeType(selectedMode)) return
    setShouldOpenDropdown(false)
    navigate(selectedMode.toLowerCase())
  }

  return (
    <Box
      ref={dropdownRef}
      sx={{
        position: 'relative',
        paddingRight: 'var(--spacing-xs)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        borderRight: '1px solid var(--border-medium)'
      }}
      data-testid="ert-mode-dropdown"
    >
      <Button
        variant="text"
        sx={{ py: 'var(--spacing-xs)' }}
        onClick={() => setShouldOpenDropdown(!shouldOpenDropdown)}
        data-testid="mode-dropdown-wrapper"
        aria-expanded={shouldOpenDropdown}
        aria-haspopup="menu"
        startIcon={getModeIcon(currentMode)}
        endIcon={<DropDownArrow aria-hidden="true" />}
      >
        <Typography>{currentMode}</Typography>
      </Button>
      {shouldOpenDropdown && (
        <Dropdown
          sx={{
            top: '90%',
            left: 0
          }}
          prompt="Select your view"
          options={authorizedModes}
          onSelect={handleModeSelect}
          getIcon={getModeIcon}
          data-testid="mode-dropdown"
        />
      )}
    </Box>
  )
}
